import React, { useState, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import MyPageLayout from '../../components/mypage/MyPageLayout';
import MyPageBreadcrumb from '../../components/mypage/MyPageBreadcrumb';
import { useMyActivity, ActivityTab, PostItem, CommentItem } from '../../hooks/useMyActivity';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';

interface MyPageActivityProps {}

const MyPageActivity: React.FC<MyPageActivityProps> = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<ActivityTab>('posts');
  const [searchQuery, setSearchQuery] = useState('');

  // 디바운스된 검색어
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

  // 검색어 디바운스 처리
  React.useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const { 
    data, 
    loading, 
    error, 
    hasMore, 
    loadMore, 
    refetch 
  } = useMyActivity({ 
    activeTab, 
    searchQuery: debouncedSearchQuery 
  });

  useInfiniteScroll({
    hasMore,
    isLoading: loading,
    onLoadMore: loadMore,
    threshold: 200,
  });

  // 검색 중인지 확인
  const isSearching = useMemo(() => {
    return debouncedSearchQuery && debouncedSearchQuery.trim().length > 0;
  }, [debouncedSearchQuery]);

  // 탭 변경 핸들러
  const handleTabChange = (tab: ActivityTab) => {
    setActiveTab(tab);
  };

  // 검색어 변경 핸들러
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // 검색 초기화
  const clearSearch = () => {
    setSearchQuery('');
    setDebouncedSearchQuery('');
  };

  // 엔터 키 처리
  const handleKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      setDebouncedSearchQuery(searchQuery);
    }
  };

  // 게시글 클릭 핸들러
  const handlePostClick = (postId: number) => {
    navigate(`/community/post/${postId}`);
  };

  // 댓글 클릭 핸들러 (댓글이 달린 게시글로 이동)
  const handleCommentClick = (comment: CommentItem) => {
    // 댓글 데이터에 postId가 있는 경우 (일반 목록)
    if (comment.postId) {
      navigate(`/community/post/${comment.postId}`);
    } else {
      // 검색 결과의 경우 postId가 없어서 이동할 수 없음
      // 알림 메시지 표시 또는 다른 처리 방법 사용
      /// console.warn('게시글 정보가 없어 이동할 수 없습니다.');
    }
  };

  // PostItem 타입 가드
  const isPostItem = (item: PostItem | CommentItem): item is PostItem => {
    return 'title' in item && 'category' in item;
  };

  // CommentItem 타입 가드  
  const isCommentItem = (item: PostItem | CommentItem): item is CommentItem => {
    return 'content' in item && 'postTitle' in item;
  };

  const renderPostItem = (post: PostItem) => (
    <PostItemContainer key={post.id} onClick={() => handlePostClick(post.id)}>
      <PostContent>
        <PostInfo>
          <PostTitle>{post.title}</PostTitle>
        </PostInfo>
        
        <PostMeta>
          <PostMetaLeft>
            <PostCategory>{post.category}</PostCategory>
            {post.lectureTitle && (
              <>
                <Divider />
                <PostLecture>{post.lectureTitle}</PostLecture>
              </>
            )}
            {post.chapter && Number(post.chapter) !== 0 && (
              <>
                <Divider />
                <PostChapter>챕터 {post.chapter}</PostChapter>
              </>
            )}
            {post.studyCategoryName && (
              <>
                <Divider />
                <PostStudy>{post.studyCategoryName}</PostStudy>
              </>
            )}
          </PostMetaLeft>
          
          <PostMetaRight>
            <PostDate>{post.date}</PostDate>
            {post.comments !== undefined && (
              <>
                <Divider />
                <PostComments>댓글 {post.comments}</PostComments>
              </>
            )}
            {post.views !== undefined && (
              <>
                <Divider />
                <PostViews>조회수 {post.views}</PostViews>
              </>
            )}
          </PostMetaRight>
        </PostMeta>
      </PostContent>
    </PostItemContainer>
  );

  const renderCommentItem = (comment: CommentItem) => (
    <CommentItemStyle key={comment.id} onClick={() => handleCommentClick(comment)}>
      <CommentContent>
        <CommentInfo>
          <CommentText>{comment.content}</CommentText>
        </CommentInfo>
        
        <CommentMeta>
          <CommentMetaLeft>
            {comment.postTitle && (
              <CommentPostTitle>{comment.postTitle}</CommentPostTitle>
            )}
          </CommentMetaLeft>
          
          <CommentMetaRight>
            <CommentDate>{comment.date}</CommentDate>
          </CommentMetaRight>
        </CommentMeta>
      </CommentContent>
    </CommentItemStyle>
  );

  return (
    <MyPageLayout>
      <Container>
        <MainContainer>
          {/* 페이지 헤더 */}
          <PageHeader>
            <PageTitle>나의 활동</PageTitle>
          </PageHeader>

          {/* 브레드크럼과 검색 */}
          <NavigationContainer>
            <MyPageBreadcrumb 
              items={[
                { label: '홈' },
                { label: '마이페이지' },
                { label: '나의 활동' }
              ]}
            />
            
            <SearchContainer>
              <SearchIcon>
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                  <path d="M7.333 12.667A5.333 5.333 0 1 0 7.333 2a5.333 5.333 0 0 0 0 10.667ZM14 14l-2.9-2.9" stroke="rgba(55, 56, 60, 0.28)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </SearchIcon>
              <SearchInput
                placeholder="검색어를 입력해주세요."
                value={searchQuery}
                onChange={handleSearchChange}
                onKeyPress={handleKeyPress}
              />
              {searchQuery && (
                <ClearButton onClick={clearSearch}>
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                    <path d="M12 4L4 12M4 4l8 8" stroke="rgba(55, 56, 60, 0.61)" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </ClearButton>
              )}
            </SearchContainer>
          </NavigationContainer>

          {/* 탭 영역 */}
          <TabSection>
            <TabContainer>
              <TabButton 
                active={activeTab === 'posts'} 
                onClick={() => handleTabChange('posts')}
              >
                작성한 글
              </TabButton>
              <TabButton 
                active={activeTab === 'comments'} 
                onClick={() => handleTabChange('comments')}
              >
                작성한 댓글
              </TabButton>
              <TabButton 
                active={activeTab === 'commented'} 
                onClick={() => handleTabChange('commented')}
              >
                댓글단 글
              </TabButton>
            </TabContainer>
          </TabSection>

          {/* 콘텐츠 영역 */}
          <ContentSection>
            {error && (
              <ErrorMessage>
                {error}
                <RetryButton onClick={refetch}>다시 시도</RetryButton>
              </ErrorMessage>
            )}

            {!error && (
              <>
                {/* 검색 결과 정보 */}
                {/* {isSearching && data.length > 0 && (
                  <SearchResultInfo>
                    '{debouncedSearchQuery}' 검색 결과 {data.length}개
                  </SearchResultInfo>
                )} */}

                {/* 목록 */}
                <PostList>
                  {Array.isArray(data) && data.map((item) => {
                    if (isPostItem(item)) {
                      return renderPostItem(item);
                    } else if (isCommentItem(item)) {
                      return renderCommentItem(item);
                    }
                    return null;
                  })}
                </PostList>

                {/* 로딩 상태 */}
                {loading && (
                  <LoadingContainer>
                    <LoadingSpinner />
                    <LoadingText>불러오는 중...</LoadingText>
                  </LoadingContainer>
                )}

                {/* 빈 상태 */}
                {!loading && (!Array.isArray(data) || data.length === 0) && (
                  <EmptyState>
                    {isSearching 
                      ? `'${debouncedSearchQuery}'에 대한 검색 결과가 없습니다.`
                      : activeTab === 'posts' 
                        ? '작성한 글이 없습니다.'
                        : activeTab === 'comments'
                        ? '작성한 댓글이 없습니다.'
                        : '댓글을 단 글이 없습니다.'
                    }
                  </EmptyState>
                )}

                {/* 무한스크롤 로딩 표시 */}
                {!isSearching && hasMore && loading && (
                  <LoadingContainer>
                    <LoadingSpinner />
                    <LoadingText>추가 데이터를 불러오는 중...</LoadingText>
                  </LoadingContainer>
                )}

                {/* 더 이상 불러올 데이터가 없을 때 */}
                {!isSearching && !hasMore && Array.isArray(data) && data.length > 0 && (
                  <EndMessage>모든 항목을 불러왔습니다.</EndMessage>
                )}
              </>
            )}
          </ContentSection>
        </MainContainer>
      </Container>
    </MyPageLayout>
  );
};

// 스타일 컴포넌트들
const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: flex-end;
  align-items: flex-start;
`;

const MainContainer = styled.div`
  width: 100%;
  max-width: 894px;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 36px;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 16px;
  margin-top: 60px;
`;

const PageTitle = styled.h1`
  color: #171719;
  font-size: 40px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 700;
  line-height: 52px;
  margin: 0;
`;

const NavigationContainer = styled.div`
  width: 100%;
  height: 32px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 32px;
  margin-top: -4px;
`;

const SearchContainer = styled.div`
  position: relative;
  width: 282px;
  height: 32px;
  display: flex;
  align-items: center;
`;

const SearchInput = styled.input`
  width: 100%;
  height: 100%;
  padding: 8px 32px 8px 32px;
  border-radius: 12px;
  border: 1px solid rgba(112, 115, 124, 0.22);
  font-size: 12px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: 0.3px;
  color: #171719;
  outline: none;
  box-sizing: border-box;
  
  &::placeholder {
    color: rgba(55, 56, 60, 0.28);
  }
  
  &:focus {
    border-color: rgba(112, 115, 124, 0.5);
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 8px;
  top: 8px;
  height: 16px;
  padding-left: 2px;
  padding-right: 2px;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1;
`;

const ClearButton = styled.button`
  position: absolute;
  right: 8px;
  top: 8px;
  height: 16px;
  width: 16px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  
  &:hover {
    opacity: 0.7;
  }
`;

const TabSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const TabContainer = styled.div`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
`;

const TabButton = styled.button<{ active: boolean }>`
  padding: 7px 11px;
  border-radius: 10px;
  border: 1px solid ${props => props.active ? 'black' : 'rgba(112, 115, 124, 0.16)'};
  background: ${props => props.active ? 'black' : 'transparent'};
  color: ${props => props.active ? '#F7F7F8' : 'rgba(55, 56, 60, 0.61)'};
  font-size: 15px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 22px;
  letter-spacing: 0.14px;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  
  &:hover {
    opacity: 0.8;
  }
`;

const ContentSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const SearchResultInfo = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 20px;
`;

const PostList = styled.div`
  display: flex;
  flex-direction: column;
`;

const PostItemContainer = styled.div`
  border-top: 1px solid rgba(112, 115, 124, 0.22);
  cursor: pointer;
  
  &:last-child {
    border-bottom: 1px solid rgba(112, 115, 124, 0.22);
  }
`;

const PostContent = styled.div`
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  transition: opacity 0.2s ease;
`;

const PostInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PostTitle = styled.h3`
  color: #171719;
  font-size: 18px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 26px;
  margin: 0;
`;

const PostMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const PostMetaLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const PostMetaRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const PostCategory = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.2px;
`;

const PostLecture = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.2px;
`;

const PostChapter = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.2px;
`;

const PostStudy = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 600;
  line-height: 20px;
  letter-spacing: 0.2px;
`;

const PostDate = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.2px;
`;

const PostComments = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.2px;
`;

const PostViews = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.2px;
`;

const Divider = styled.div`
  width: 1px;
  height: 12px;
  background: rgba(112, 115, 124, 0.22);
`;

// 댓글 관련 스타일 컴포넌트
const CommentItemStyle = styled.div`
  border-top: 1px solid rgba(112, 115, 124, 0.22);
  cursor: pointer;
  
  &:last-child {
    border-bottom: 1px solid rgba(112, 115, 124, 0.22);
  }
`;

const CommentContent = styled.div`
  padding: 32px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  transition: opacity 0.2s ease;
`;

const CommentInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const CommentText = styled.p`
  color: #171719;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
  letter-spacing: 0.09px;
  margin: 0;
`;

const CommentMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
`;

const CommentMetaLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
`;

const CommentMetaRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const CommentPostTitle = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  line-height: 20px;
  letter-spacing: 0.2px;
  max-width: 600px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CommentDate = styled.span`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 20px;
  letter-spacing: 0.2px;
`;

// 상태 관련 스타일
const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px 0;
`;

const LoadingSpinner = styled.div`
  width: 32px;
  height: 32px;
  border: 3px solid rgba(112, 115, 124, 0.22);
  border-top: 3px solid #171719;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 80px 20px;
  color: rgba(55, 56, 60, 0.61);
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  line-height: 24px;
`;

const EndMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: rgba(55, 56, 60, 0.61);
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
`;

const ErrorMessage = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 40px 20px;
  color: #dc3545;
  font-size: 16px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 400;
  text-align: center;
`;

const RetryButton = styled.button`
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid #dc3545;
  background: transparent;
  color: #dc3545;
  font-size: 14px;
  font-family: 'Pretendard JP', sans-serif;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background: #dc3545;
    color: white;
  }
`;

export default MyPageActivity;
